import { useCallback } from 'react';
import { FieldValues, useController } from 'react-hook-form';
import { Field, FieldProps as SWFieldProps } from '@spark-web/field';
import { TextArea, TextAreaProps } from '@spark-web/text-area';
import type { FormFieldProps } from '../Form';

export type TextAreaFieldProps<TFieldValues extends FieldValues = FieldValues> = FormFieldProps<TFieldValues> &
  TextAreaProps & { FieldProps?: Omit<SWFieldProps, 'label' | 'children'> } & {
    maxLength?: number;
  };

function TextAreaField<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  control,
  shouldUnregister,
  FieldProps,
  maxLength,
  ...textAreaProps
}: TextAreaFieldProps<TFieldValues>) {
  const {
    field,
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules: {
      onBlur: textAreaProps?.onBlur,
      onChange: textAreaProps?.onChange,
    },
    shouldUnregister,
  });

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = event.target.value;
    if (maxLength && value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    field.onChange(value);
  };

  const textAreaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      field.ref(node);
      if (node) {
        node.style.whiteSpace = 'pre-wrap';
        node.style.overflowWrap = 'break-word';
        node.style.wordBreak = 'break-word';
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field.ref],
  );

  return (
    <Field
      label={label}
      {...FieldProps}
      tone={error ? 'critical' : FieldProps?.tone}
      message={error?.message ?? FieldProps?.message}
    >
      <TextArea {...textAreaProps} {...field} ref={textAreaRef} onChange={handleChange} />
    </Field>
  );
}

export default TextAreaField;
